import {Button, DataStatus, EmptyStatus, openDialog} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {useEffect, useRef} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetBusinessCaseQuery, useListBusinessCaseTestDrivesQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {Main, RestrictedArea, usePermissions} from '@dms/shared';

import {isNotNilOrEmpty, parseDate, useQueryState, useRequiredParams} from 'shared/web';

import {businessCaseSearchParams} from '../../businessCaseSearchParams';
import {TestDriveCard} from './components/TestDriveCard';
import {TestDriveDialog} from './components/TestDriveDialog';

export function BusinessCaseTestDriveV2() {
  const {id: businessCaseId} = useRequiredParams();

  const {
    data: businessCase,
    isLoading: isBusinessCaseLoading,
    isError: isBusinessCaseError,
  } = useGetBusinessCaseQuery({businessCaseId});

  const {data: testDrives, isLoading: areTestDrivesLoading} = useListBusinessCaseTestDrivesQuery({
    businessCaseId,
  });

  const [hasReadTestDrivePermission, hasCreateTestDrivePermission] = usePermissions({
    permissionKeys: ['readTestDrive', 'createTestDrive'],
  });

  const [editTestDriveId, , clearEditTestDriveId] = useQueryState(
    businessCaseSearchParams.testDriveEdit
  );

  const processedEditIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editTestDriveId || !testDrives || areTestDrivesLoading) {
      return;
    }

    if (
      isNotNilOrEmpty(processedEditIdRef.current) &&
      processedEditIdRef.current === editTestDriveId
    ) {
      return;
    }

    const targetTestDrive = testDrives.find((td) => td.id === editTestDriveId);

    if (!targetTestDrive) {
      return;
    }

    processedEditIdRef.current = editTestDriveId;

    openDialog(
      <TestDriveDialog
        businessCaseId={businessCaseId}
        vehicleId={targetTestDrive.vehicleId}
        testDrive={targetTestDrive}
      />,
      {
        id: 'TEST_DRIVE_DIALOG',
        title: i18n.t('entity.testDrive.labels.editTestDrive'),
        size: 'large',
        withAdditionalFooter: true,
        onCloseComplete() {
          processedEditIdRef.current = null;
          clearEditTestDriveId();
        },
      }
    );

    return () => {
      processedEditIdRef.current = null;
    };
  }, [editTestDriveId, testDrives, areTestDrivesLoading, businessCaseId, clearEditTestDriveId]);

  const handleNewTestDriveDialogOpen = () => {
    openDialog(
      <TestDriveDialog
        vehicleId={businessCase?.offers?.[0]?.saleVehicles?.[0]?.vehicleId}
        businessCaseId={businessCaseId}
      />,
      {
        title: i18n.t('entity.testDrive.labels.createTestDrive'),
        size: 'large',
        withAdditionalFooter: true,
      }
    );
  };

  const isLoading = isBusinessCaseLoading || areTestDrivesLoading;
  const isError = isBusinessCaseError;

  const sortedTestDrives = testDrives
    ? [...testDrives].sort(
        (a, b) => parseDate(b.scheduledAt).getTime() - parseDate(a.scheduledAt).getTime()
      )
    : [];

  return (
    <Main>
      <DataStatus isLoading={isLoading} isError={isError}>
        <VStack spacing={4}>
          <Show when={isNotNilOrEmpty(testDrives)}>
            <HStack width="100%" align="center" justify="flex-end">
              <Button
                title={i18n.t('entity.testDrive.labels.createTestDrive')}
                isDisabled={!hasCreateTestDrivePermission}
                onClick={handleNewTestDriveDialogOpen}
              />
            </HStack>

            <Show when={hasReadTestDrivePermission}>
              <DataStatus isLoading={isLoading} isError={isError}>
                {sortedTestDrives.map((testDrive) => (
                  <TestDriveCard
                    testDrive={testDrive}
                    key={testDrive.id}
                    businessCaseId={businessCaseId}
                  />
                ))}
              </DataStatus>
            </Show>

            <Show when={!hasReadTestDrivePermission}>
              <RestrictedArea shouldHideRedirectButton />
            </Show>
          </Show>

          <Show when={isNilOrEmpty(testDrives)}>
            <Box padding={11}>
              <EmptyStatus
                variant="vehicle"
                headline={i18n.t('entity.testDrive.labels.noTestDriveForOpportunity')}
                action={{
                  title: i18n.t('entity.testDrive.labels.scheduleTestDrive'),
                  variant: 'primary',
                  leftIcon: 'content/add_circle',
                  onClick: handleNewTestDriveDialogOpen,
                  isDisabled: !hasCreateTestDrivePermission,
                }}
              />
            </Box>
          </Show>
        </VStack>
      </DataStatus>
    </Main>
  );
}
